import { computed } from 'vue'
import { enumValue, useKsI18n } from '@siclop/kstone-vue'
import { RttMode } from '../enums/RttMode'

export interface RttModeOption {
  value: RttMode
  label: string
  /** Phrase d'aide affichée sous le sélecteur. */
  help: string
}

/**
 * Valeur du sélecteur « Mode RTT » de la fiche salarié pour « Selon l'entreprise » (`rttMode = null`). Un sélecteur
 * ne distingue pas bien null de 0 (« Aucun ») : on passe par cette valeur sentinelle.
 */
export const RTT_MODE_COMPANY_DEFAULT = -1

/** Mode RTT connu (nombre ou nom de l'API), sinon null. */
function knownRttMode(value: unknown): RttMode | null {
  if (value === null || value === undefined || value === '') return null
  const mode = enumValue(RttMode, value)
  return typeof mode === 'number' && typeof RttMode[mode] === 'string' ? mode : null
}

/** `rttMode` du salarié → valeur du sélecteur (null / absent → « Selon l'entreprise », 0 reste « Aucun »). */
export function toEmployeeRttSelectValue(mode: unknown): number {
  return knownRttMode(mode) ?? RTT_MODE_COMPANY_DEFAULT
}

/** Valeur du sélecteur → `rttMode` du salarié (null = mode de l'entreprise). */
export function fromEmployeeRttSelectValue(value: unknown): RttMode | null {
  return value === RTT_MODE_COMPANY_DEFAULT ? null : knownRttMode(value)
}

/**
 * Modes de calcul des RTT (libellés et aides), partagés par les paramètres de l'entreprise et la fiche salarié
 * (mode propre au salarié, `null` = celui de l'entreprise).
 */
export function useRttModeOptions() {
  const { t } = useKsI18n()

  const options = computed<RttModeOption[]>(() => [
    {
      value: RttMode.None,
      label: t('ksproject.hr.leaveSettings.rttMode.none', 'Aucun'),
      help: t('ksproject.hr.leaveSettings.rttMode.noneHelp', 'Pas de RTT : aucun solde de RTT n\'est ouvert.')
    },
    {
      value: RttMode.FixedDays,
      label: t('ksproject.hr.leaveSettings.rttMode.fixedDays', 'Forfait annuel'),
      help: t('ksproject.hr.leaveSettings.rttMode.fixedDaysHelp', 'Un nombre fixe de jours de RTT par an, disponibles dès le 1er janvier (ou l\'embauche), au prorata des mois sous contrat.')
    },
    {
      value: RttMode.HoursAbove35,
      label: t('ksproject.hr.leaveSettings.rttMode.hoursAbove35', 'Heures au-delà de 35 h'),
      help: t('ksproject.hr.leaveSettings.rttMode.hoursAbove35Help', 'Les RTT compensent la durée hebdomadaire du salarié (fiche salarié) au-delà de 35 h.')
    },
    {
      value: RttMode.ForfaitJours,
      label: t('ksproject.hr.leaveSettings.rttMode.forfaitJours', 'Forfait jours'),
      help: t('ksproject.hr.leaveSettings.rttMode.forfaitJoursHelp', 'Cadres au forfait : jours ouvrés de l\'année − jours fériés − congés payés − base du forfait (2026 : 261 − 9 − 25 − 218 = 9 jours).')
    }
  ])

  /** Option d'un mode (nombre ou nom renvoyé par l'API) ; undefined si inconnu ou null. */
  function find(mode: unknown): RttModeOption | undefined {
    if (mode === null || mode === undefined || mode === '') return undefined
    const value = enumValue(RttMode, mode)
    return options.value.find(o => o.value === value)
  }

  return { options, find }
}
